import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { IoChevronDown } from "react-icons/io5";
import { fetchGalleryPosts } from "../../features/gallery/GalleryServices";
import { useLocation, useNavigate } from "react-router-dom";
import { useOutsideClick } from "../../util/hooks/useDebounce";

const TYPE_OPTIONS = ["All", "Image", "Video", "Document"];

interface GalleryListProps {
    onLoadingChange?: (loading: boolean) => void;
}

export function GalleryList({ onLoadingChange }: GalleryListProps) {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    useOutsideClick(dropdownRef, () => setOpen(false));

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState<string[]>(["All"]);

    // ✅ Mode
    const isSelectionMode = location.state?.mode === "select";
    const returnTo = location.state?.returnTo;

    // ✅ Track selected item (single select)
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const typeMap: any = {
        Image: "IMAGE",
        Video: "VIDEO",
        Document: "DOCUMENT",
    };

    const handleSelect = (item: any) => {
        if (!isSelectionMode || !returnTo) return;

        setSelectedId(item.id);

        // small delay for UX feel
        setTimeout(() => {
            // navigate(returnTo, {
            //     state: {
            //         selectedMedia: item
            //     }
            // });
            navigate(returnTo, {
                state: {
                    ...(location.state?.returnState || {}),
                    selectedMedia: item,
                },
            });
        }, 150);
    };

    const handleTypeToggle = (type: string) => {
        if (type === "All") {
            setSelectedTypes(["All"]);
            return;
        }

        let updated = [...selectedTypes.filter((t) => t !== "All")];

        if (updated.includes(type)) {
            updated = updated.filter((t) => t !== type);
        } else {
            updated.push(type);
        }

        if (updated.length === 0) updated = ["All"];

        setSelectedTypes(updated);
    };

    const loadGallery = async () => {
        setLoading(true);
        onLoadingChange?.(true);
        try {
            const apiType = selectedTypes.includes("All")
                ? undefined
                : typeMap[selectedTypes[0]];

            const res = await dispatch(
                fetchGalleryPosts({ mediaType: apiType })
            ).unwrap();

            setData(res.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    useEffect(() => {
        loadGallery();
    }, [selectedTypes]);

    return (
        <>
            <div className="flex flex-col items-end justify-end sm:flex-row gap-4 sm:items-center relative mb-10">
                <div ref={dropdownRef} className="p-[2px] rounded-4xl bg-brand-blue-gradient w-full sm:w-auto relative">
                    <button
                        onClick={() => setOpen(!open)}
                        className="flex w-full sm:w-auto items-center gap-2 px-5 py-2 rounded-4xl bg-white font-semibold text-text-secondary"
                    >
                        {selectedTypes.includes("All")
                            ? "Filter by Type"
                            : selectedTypes.join(", ")}
                        <IoChevronDown className="ml-auto" />
                    </button>

                    {open && (
                        <div className="absolute mt-2 w-full sm:w-56 bg-white border rounded-xl shadow z-20 p-2">
                            {TYPE_OPTIONS.map((item) => {
                                const checked = selectedTypes.includes(item);
                                return (
                                    <div
                                        key={item}
                                        onClick={() => handleTypeToggle(item)}
                                        className="flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-100 rounded-md"
                                    >
                                        <input type="checkbox" checked={checked} readOnly />
                                        <span>{item}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => navigate("/admin/gallery/add")}
                    className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap w-full sm:w-auto"
                >
                    + Add Image
                </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {data.map((item: any) => {
                    const isSelected = selectedId === item.id;

                    return (
                        <div
                            key={item.id}
                            onClick={() => handleSelect(item)}
                            className={`relative border overflow-hidden transition rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-md
                                ${isSelectionMode ? "cursor-pointer " : ""}
                                ${isSelected ? "border-brand-main scale-[1.02]" : ""}
                            `}
                        >
                            {isSelectionMode && (
                                <div
                                    className="absolute top-2 right-2 z-10"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(item);
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        readOnly
                                        className="w-4 h-4 accent-brand-main cursor-pointer"
                                    />
                                </div>
                            )}

                            {/* Media */}
                            <div className="h-[300px] bg-white flex items-center justify-center">
                                {item.mediaType === "IMAGE" ? (
                                    <img src={item.mediaUrl} className="w-full h-full object-contain transition-transform duration-300 ease-in-out hover:scale-110" />
                                ) : item.mediaType === "VIDEO" ? (
                                    <video src={item.mediaUrl} className="w-full h-full object-cover" />
                                ) : (
                                    <span>📄 Document</span>
                                )}
                            </div>

                            <div className="p-3 bg-gray-50 flex flex-col gap-1">
                                <p className="text-sm font-bold text-brand-main truncate">
                                    Template Name: {item.templateName}
                                </p>
                                <p className="text-xs text-text-secondary font-semibold">
                                    Type: {item.mediaType?.charAt(0) + item.mediaType?.slice(1).toLowerCase()}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}
